import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useApi } from '@/lib/api-context';

// Marcas de latido tomadas sin conexión (so=420, spec §4.1). Se descargan con el flush al
// volver la conexión o tras el siguiente latido exitoso. Al dejar un flujo (cambio o
// desmontaje) sus marcas se descargan con SU id: nunca se atribuyen a otro flujo.

const FLUSH = '/api/v1/quality/time-engine/heartbeats/flush';
/** Tope de marcas guardadas sin conexión, igual que el del servidor. */
export const MAX_MARKS = 5000;

export interface OfflineMarks {
  pendingMarks: number;
  /** Guarda el instante de un latido que no llegó (conserva las más recientes). */
  add: (at: Date) => void;
  /** Descarga las marcas pendientes del flujo actual; si falla, se reintenta después. */
  flush: () => Promise<void>;
}

export function useOfflineMarks(flowInstanceId: string, maxMarks = MAX_MARKS): OfflineMarks {
  const api = useApi();
  const [pendingMarks, setPendingMarks] = useState(0);
  const marksRef = useRef<string[]>([]);
  const flowRef = useRef(flowInstanceId);
  const maxRef = useRef(maxMarks);

  useEffect(() => {
    flowRef.current = flowInstanceId;
    maxRef.current = maxMarks;
  });

  const flush = useCallback(async (): Promise<void> => {
    const marks = marksRef.current.slice();
    if (marks.length === 0) {
      return;
    }
    try {
      await api.post(FLUSH, { flowInstanceId: flowRef.current, marks });
      marksRef.current = marksRef.current.slice(marks.length);
      setPendingMarks(marksRef.current.length);
    } catch {
      // Se reintenta con el siguiente latido exitoso o al volver la conexión.
    }
  }, [api]);

  const add = useCallback((at: Date): void => {
    marksRef.current = [...marksRef.current, at.toISOString()].slice(-maxRef.current);
    setPendingMarks(marksRef.current.length);
  }, []);

  useEffect(() => {
    const onOnline = (): void => void flush();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [flush]);

  useEffect(() => {
    const flow = flowInstanceId;
    return () => {
      const marks = marksRef.current;
      if (marks.length === 0) {
        return;
      }
      marksRef.current = [];
      setPendingMarks(0);
      // Mejor esfuerzo: una falla (incluso síncrona) no debe escapar del desmontaje.
      void Promise.resolve()
        .then(() => api.post(FLUSH, { flowInstanceId: flow, marks }))
        .catch(() => undefined);
    };
  }, [flowInstanceId, api]);

  // Objeto estable mientras no cambie el conteo: quien lo usa no se recrea en cada render.
  return useMemo(() => ({ pendingMarks, add, flush }), [pendingMarks, add, flush]);
}
